import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useParams, useHistory, generatePath } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import SearchIcon from '@material-ui/icons/Search';
import FilterListIcon from '@material-ui/icons/FilterList';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import UndoIcon from '@material-ui/icons/Undo';
import IconButton from '@material-ui/core/IconButton';
import Badge from '@material-ui/core/Badge';
import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';

import GlobalStyles from '../../../../styles/GlobalStyles.web';
import { BusinessRouterPaths } from '../../../page/Routes/RouterPaths';
import { handleError, checkStatus, parseJSON } from '../../../shared/api/core';
import { getCustomerOrderDetail } from '../../api';
import {
  saveCustomerOrderSummaryItems,
  saveSelectedCustomerOrder,
  saveCustomerOrderConflictRes,
} from '../../../master-data/data/actions/customerOrderActions';
import {
  mapItemDetails,
  initCGTree,
  initConflictItems,
  applyPickingFiltersResult,
  mergeFilteredIntoEdited,
} from '../../lib/orderPickingItems';
import type { CommodityGroupTreeNode } from '../../lib/orderPickingItems';
import {
  createOrderPickingFiltersSet1,
  createOrderPickingFiltersSet2,
} from '../../lib/orderPickingConstants';
import type { PickingFilterChip } from '../../lib/orderPickingConstants';
import CommodityGroupTree from '../../components/CommodityGroupTree/CommodityGroupTree.web';
import OrderDetailLineCard from '../../components/OrderDetailLineCard/OrderDetailLineCard.web';
import { ToastError, ToastSuccess, enqueueSnackbar } from '../../../shared/components/Notifier';

const CONFLICT_MSG =
  'The order has changed since the last sync with Korona, please review and fix the conflicts.';

const OrderPickingDetail = (props: any) => {
  const { orderId } = useParams<{ orderId: string }>();
  const history = useHistory();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(true);
  const [orderDetail, setOrderDetail] = useState<any>(null);
  const [editedItems, setEditedItems] = useState<any[] | null>(null);
  const [deletedItems, setDeletedItems] = useState<any[] | null>(null);
  const [conflictedItems, setConflictedItems] = useState<any[]>([]);
  const [commodityGroupTree, setCommodityGroupTree] = useState<CommodityGroupTreeNode[]>([]);
  const [origCommodityGroupTree, setOrigCommodityGroupTree] = useState<CommodityGroupTreeNode[]>([]);
  const [canFinalizeOrder, setCanFinalizeOrder] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredItems, setFilteredItems] = useState<any[] | null>(null);
  const [filtersSet1, setFiltersSet1] = useState<PickingFilterChip[]>(() =>
    cloneDeep(createOrderPickingFiltersSet1()),
  );
  const [filtersSet2, setFiltersSet2] = useState<PickingFilterChip[]>(() =>
    cloneDeep(createOrderPickingFiltersSet2()),
  );
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [activeFilterCount, setActiveFilterCount] = useState(0);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [searchShown, setSearchShown] = useState(false);

  const sb = props.userData?.selectedBusiness;
  const locationId = props.userData?.selectedLocations?.[0]?.id;

  const applyCachedPickedState = (baseItems: any[]): any[] => {
    const cached = props.customerOrders?.customerOrderSummaryItems?.products;
    if (!cached || !cached.length) {
      return baseItems;
    }
    const byId = new Map<number, any>(
      cached.map((c: any) => [c.id, c] as [number, any]),
    );
    return baseItems.map((item: any) => {
      const cachedItem = byId.get(item.id);
      if (!cachedItem) {
        return item;
      }
      const next = { ...item };
      next.pickedQty = cachedItem.pickedQty;
      next.confirmed = cachedItem.confirmed;
      next.pickedAll = cachedItem.pickedAll;
      next.disabled = cachedItem.disabled;
      const cachedCodes = cachedItem.codePickedQtys || [];
      next.codePickedQtys = (next.codePickedQtys || []).map((code: any) => {
        const matched = cachedCodes.find(
          (c: any) => c.product_code === code.product_code && c.case_quantity === code.case_quantity,
        );
        return matched ? { ...code, pickedQty: matched.pickedQty } : code;
      });
      return next;
    });
  };

  const loadDetail = () => {
    if (!sb || !locationId || !orderId) {
      return;
    }
    const selected = props.customerOrders?.selectedCustomerOrder?.item;
    const businessId = selected?.business ?? sb.business_id;
    const loc = selected?.location ?? locationId;
    setLoading(true);
    getCustomerOrderDetail(orderId, businessId, String(loc))
      .catch(handleError)
      .then(checkStatus)
      .then(parseJSON)
      .then((res: any) => {
        const order_details = res.order_data;
        const items = order_details.items;
        const canFin = res.pick_group ? res.pick_group.can_finalize_order : false;
        setCanFinalizeOrder(!!canFin);

        const updatedData = items.filter((i: any) => !i.is_deleted).map((i: any) => mapItemDetails(i));
        const hydratedData = applyCachedPickedState(updatedData);
        const deletedData = items.filter((i: any) => i.is_deleted).map((i: any) => mapItemDetails(i));
        let cg_tree: CommodityGroupTreeNode[] = res.commodity_group_tree || [];
        cg_tree = initCGTree(cg_tree, hydratedData);
        setOrigCommodityGroupTree(cloneDeep(cg_tree));
        setCommodityGroupTree(cg_tree);

        const result = initConflictItems(res, items, hydratedData, false);
        const cItems = result.conflicted;
        const eItems = result.editedItems;
        order_details.updatedItems = eItems;
        order_details.deletedItems = deletedData;
        setOrderDetail(order_details);
        setEditedItems(eItems);
        setDeletedItems(deletedData);
        setConflictedItems(cItems);
        setFilteredItems(null);
        setSearchTerm('');
        setLoading(false);

        if (order_details.is_finalized) {
          const path = generatePath(BusinessRouterPaths.customerOrderPickingSummary, {
            orderId: String(orderId),
          });
          history.replace(path);
        }
        if (res.status === 'conflicted') {
          props.showToast({
            message: CONFLICT_MSG,
            options: ToastError,
          });
        }
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    loadDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, sb?.business_id, locationId]);

  useEffect(() => {
    const cr = props.customerOrders?.customerOrderConflictRes;
    if (!cr) {
      return;
    }
    const ei = editedItems;
    if (!ei) {
      return;
    }
    const data = cr;
    const order_details = data.fresh_order_data;
    let cg_tree: CommodityGroupTreeNode[] = data.commodity_group_tree || [];
    const freshItems = order_details.items;
    const deletedData = freshItems.filter((i: any) => i.is_deleted).map((i: any) => mapItemDetails(i));
    for (const dd of deletedData) {
      for (const row of ei) {
        if (row.id === dd.id) {
          dd.pickedQty = row.pickedQty;
        }
      }
    }
    if (data.status === 'conflicted') {
      const result = initConflictItems(data, freshItems, ei, true);
      const cItems = result.conflicted;
      const eItems = result.editedItems;
      order_details.updatedItems = eItems;
      order_details.deletedItems = deletedData;
      setOrderDetail(order_details);
      setEditedItems(eItems);
      setDeletedItems(deletedData);
      setConflictedItems(cItems);
      cg_tree = initCGTree(cg_tree, eItems);
      setCommodityGroupTree(cg_tree);
      setOrigCommodityGroupTree(cloneDeep(cg_tree));
      props.showToast({ message: CONFLICT_MSG, options: ToastError });
    }
    props.saveCustomerOrderConflictRes(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.customerOrders?.customerOrderConflictRes]);


  const applyFiltersInternal = (closeModal = false) => {
    if (!editedItems) {
      return;
    }
    const resultant = applyPickingFiltersResult({
      editedItems,
      filtersSet1,
      filtersSet2,
      searchTerm,
    });
    if (resultant === null) {
      setFilteredItems(null);
      const cg = initCGTree(origCommodityGroupTree, editedItems);
      setCommodityGroupTree(cg);
    } else {
      setFilteredItems(resultant);
      const cg = initCGTree(origCommodityGroupTree, resultant);
      setCommodityGroupTree(cg);
    }
    const c1 = filtersSet1.filter((f) => f.isSelected).length;
    const c2 = filtersSet2.filter((f) => f.isSelected).length;
    setActiveFilterCount(c1 + c2);
    if (closeModal) {
      setFilterModalOpen(false);
    }
  };

  useEffect(() => {
    if (!editedItems || !origCommodityGroupTree?.length) {
      return;
    }
    applyFiltersInternal(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, editedItems, filtersSet1, filtersSet2, origCommodityGroupTree]);

  const editItem = (item: any) => {
    if (editedItems) {
      const eix = editedItems.findIndex((ns) => ns.id === item.id);
      if (eix >= 0) {
        const enext = [...editedItems];
        enext[eix] = item;
        setEditedItems(enext);
      }
    }
    if (filteredItems && filteredItems.length > 0) {
      const ix = filteredItems.findIndex((ns) => ns.id === item.id);
      if (ix >= 0) {
        const next = [...filteredItems];
        next[ix] = item;
        setFilteredItems(next);
      }
    }
  };

  const getItemsForCG = (cg_id: string) => {
    const list = filteredItems && filteredItems.length > 0 ? filteredItems : editedItems || [];
    return list
      .filter((item) => item.product?.commodity_group?.uuid === cg_id)
      .sort((a, b) => (a.product?.name || '').localeCompare(b.product?.name || ''));
  };

  const navigateSummary = () => {
    const merged = mergeFilteredIntoEdited(filteredItems, editedItems);
    const data = merged || editedItems || [];
    props.saveCustomerOrderSummaryItems({
      products: data.map((i: any) => JSON.parse(JSON.stringify(i))),
      deletedProducts: deletedItems,
      canFinalizeOrder,
    });
    const path = generatePath(BusinessRouterPaths.customerOrderPickingSummary, {
      orderId: String(orderId),
    });
    history.push(path, {
      summarySnapshot: data,
      summaryDeletedSnapshot: deletedItems || [],
      canFinalizeOrder: !!canFinalizeOrder,
    });
  };

  const resetFiltersOnly = () => {
    setFiltersSet1(cloneDeep(createOrderPickingFiltersSet1()));
    setFiltersSet2(cloneDeep(createOrderPickingFiltersSet2()));
  };

  const resetAll = () => {
    resetFiltersOnly();
    setResetModalOpen(false);
    loadDetail();
  };

  const toggleFilterSet1 = (id: number) => {
    setFiltersSet1((prev) =>
      prev.map((f) => (f.id === id ? { ...f, isSelected: !f.isSelected } : f)),
    );
  };
  const toggleFilterSet2 = (id: number) => {
    setFiltersSet2((prev) =>
      prev.map((f) => (f.id === id ? { ...f, isSelected: !f.isSelected } : f)),
    );
  };

  const getTotalItemsCount = () => editedItems?.length ?? 0;
  const getTotalPickedItems = () =>
    editedItems?.filter((e) => Number(e.pickedQty) > 0).length ?? 0;
  const getDeletedItemsCount = () => deletedItems?.length ?? 0;
  const getConflictedItemsCount = () => conflictedItems?.length ?? 0;

  if (!sb || !locationId) {
    return (
      <Box p={2}>
        <Typography>Select a business and location.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        px={1}
        py={1}
        bgcolor={GlobalStyles.primaryColor}
        color="#fff"
      >
        <IconButton color="inherit" onClick={() => history.push(BusinessRouterPaths.customerOrderList)}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="subtitle1" style={{ flex: 1 }}>
          Order #{orderDetail?.order_number ?? orderId}
        </Typography>
        <IconButton color="inherit" onClick={() => setSearchShown(!searchShown)}>
          <SearchIcon />
        </IconButton>
        <IconButton color="inherit" onClick={() => setFilterModalOpen(true)}>
          <Badge badgeContent={activeFilterCount} color="secondary" overlap="rectangle">
            <FilterListIcon />
          </Badge>
        </IconButton>
        <IconButton color="inherit" onClick={() => setResetModalOpen(true)}>
          <UndoIcon />
        </IconButton>
        <IconButton color="inherit" onClick={navigateSummary}>
          <NavigateNextIcon />
        </IconButton>
      </Box>

      {searchShown && (
        <Box px={2} py={1}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Search product / code"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Box>
      )}

      {!isSmDown && (
        <Box px={2} py={0.5} bgcolor={GlobalStyles.infoBackground} fontSize={12}>
          Items: total {getTotalItemsCount()}, picked {getTotalPickedItems()} · Deleted{' '}
          {getDeletedItemsCount()}, changed {getConflictedItemsCount()}
        </Box>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      )}

      {!loading && orderDetail && (
        <Box px={isSmDown ? 1 : 2} py={1}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Order details</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">
                Number: {orderDetail.order_number}
              </Typography>
              <Typography variant="body2">
                Created: {moment(orderDetail.created_time).format('YYYY/MM/DD HH:mm')}
              </Typography>
              <Typography variant="body2">
                Net total: {orderDetail.net_total != null ? String(orderDetail.net_total) : '—'}
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Items</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <CommodityGroupTree
                tree={commodityGroupTree}
                renderItems={(cgId) => (
                  <Box>
                    {getItemsForCG(cgId).map((item, idx) => (
                      <OrderDetailLineCard
                        key={item.id}
                        item={item}
                        index={idx}
                        onItemChange={editItem}
                      />
                    ))}
                  </Box>
                )}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Deleted items ({deletedItems?.length ?? 0})</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {deletedItems?.map((item, idx) => (
                <OrderDetailLineCard
                  key={item.id}
                  item={item}
                  index={idx}
                  onItemChange={editItem}
                />
              ))}
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Changed / conflicts ({conflictedItems.length})</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {conflictedItems.map((item, idx) => (
                <OrderDetailLineCard
                  key={`c-${item.id}`}
                  item={item}
                  index={idx}
                  onItemChange={editItem}
                />
              ))}
            </AccordionDetails>
          </Accordion>
        </Box>
      )}

      <Dialog open={filterModalOpen} onClose={() => setFilterModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Item filters</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2">Done picking</Typography>
          <Box display="flex" flexWrap="wrap">
            {filtersSet1.map((f) => (
              <FormControlLabel
                key={f.id}
                control={
                  <Checkbox
                    checked={f.isSelected}
                    onChange={() => toggleFilterSet1(f.id)}
                    color="primary"
                  />
                }
                label={f.name}
              />
            ))}
          </Box>
          <Typography variant="subtitle2" style={{ marginTop: 8 }}>
            Picked qty
          </Typography>
          <Box display="flex" flexWrap="wrap">
            {filtersSet2.map((f) => (
              <FormControlLabel
                key={f.id}
                control={
                  <Checkbox
                    checked={f.isSelected}
                    onChange={() => toggleFilterSet2(f.id)}
                    color="primary"
                  />
                }
                label={f.name}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetFiltersOnly}>Reset</Button>
          <Button onClick={() => setFilterModalOpen(false)}>Cancel</Button>
          <Button color="primary" variant="contained" onClick={() => applyFiltersInternal(true)}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resetModalOpen} onClose={() => setResetModalOpen(false)}>
        <DialogTitle>Reset order view?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Reload all records and order status from the server?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetModalOpen(false)}>No</Button>
          <Button color="primary" variant="contained" onClick={resetAll}>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const mapState = (state: any) => ({
  userData: state.userLocalData,
  customerOrders: state.customerOrderModuleData?.customerOrderList,
});

const mapDispatch = (dispatch: any) => ({
  saveCustomerOrderSummaryItems: (d: any) => dispatch(saveCustomerOrderSummaryItems(d)),
  saveSelectedCustomerOrder: (d: any) => dispatch(saveSelectedCustomerOrder(d)),
  saveCustomerOrderConflictRes: (d: any) => dispatch(saveCustomerOrderConflictRes(d)),
  showToast: (t: any) => dispatch(enqueueSnackbar(t)),
});

export default connect(mapState, mapDispatch)(OrderPickingDetail);
